import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Divider
} from '@mui/material';
import { Analytics as AnalyticsIcon } from '@mui/icons-material';
import PromptEditor from '../PromptEditor';

interface FileItem {
  label: string;
  value: string;
  path: string;
  isSample: boolean;
}

interface FileSelectionFormProps {
  clientId: string;
  onClientIdChange: (value: string) => void;
  clientIdDisabled: boolean;
  selectedTopicFrameworkFile: string;
  onTopicFrameworkFileChange: (value: string) => void;
  topicFrameworkFiles: FileItem[];
  selectedRelevantKeywordsFile: string;
  onRelevantKeywordsFileChange: (value: string) => void;
  relevantKeywordsFiles: FileItem[];
  isLoadingFiles: boolean;
  usingMockData: boolean;
  forceReanalysis: boolean;
  onForceReanalysisChange: (value: boolean) => void;
  error: string | null;
  isAnalyzing: boolean;
  onSubmit: (e: React.FormEvent) => void;
  customPrompt?: string | null;
  onCustomPromptChange?: (prompt: string | null) => void;
}

const FileSelectionForm: React.FC<FileSelectionFormProps> = ({
  clientId,
  onClientIdChange,
  clientIdDisabled,
  selectedTopicFrameworkFile,
  onTopicFrameworkFileChange,
  topicFrameworkFiles,
  selectedRelevantKeywordsFile,
  onRelevantKeywordsFileChange,
  relevantKeywordsFiles,
  isLoadingFiles,
  usingMockData,
  forceReanalysis,
  onForceReanalysisChange,
  error,
  isAnalyzing,
  onSubmit,
  customPrompt,
  onCustomPromptChange
}) => {
  return (
    <Box component="form" onSubmit={onSubmit} sx={{ mt: 2 }}>
      <TextField
        label="Client ID"
        value={clientId}
        onChange={(e) => onClientIdChange(e.target.value)}
        placeholder="Enter client ID"
        fullWidth
        required
        margin="normal"
        disabled={clientIdDisabled}
      />
      
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Input Files from Previous Modules
        </Typography>
        
        {usingMockData && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Using fallback paths.</strong> The file list API returned an error or no files were found. Using default paths instead.
            </Typography>
          </Alert>
        )}
        
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel id="topic-framework-file-label">Topic Framework File (Module 1)</InputLabel>
          <Select
            labelId="topic-framework-file-label"
            value={topicFrameworkFiles.some(file => file.path === selectedTopicFrameworkFile) ? selectedTopicFrameworkFile : ''}
            label="Topic Framework File (Module 1)"
            onChange={(e) => onTopicFrameworkFileChange(e.target.value)}
            disabled={isLoadingFiles || topicFrameworkFiles.length === 0}
            required
          >
            {topicFrameworkFiles.map((file) => (
              <MenuItem 
                key={file.value} 
                value={file.path}
                sx={file.isSample ? {
                  color: 'text.secondary',
                  fontStyle: 'italic'
                } : {}}
              >
                {file.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {isLoadingFiles ? 'Loading files...' : 
             topicFrameworkFiles.length > 0 ? 
             `${topicFrameworkFiles.length} framework file(s) available${usingMockData ? ' (using fallback paths)' : ''}` : 
             'No framework files found'}
          </FormHelperText>
        </FormControl>
        
        <FormControl fullWidth margin="normal" size="small">
          <InputLabel id="relevant-keywords-file-label">Relevant Keywords File (Module 2)</InputLabel>
          <Select
            labelId="relevant-keywords-file-label"
            value={relevantKeywordsFiles.some(file => file.path === selectedRelevantKeywordsFile) ? selectedRelevantKeywordsFile : ''}
            label="Relevant Keywords File (Module 2)"
            onChange={(e) => onRelevantKeywordsFileChange(e.target.value)}
            disabled={isLoadingFiles || relevantKeywordsFiles.length === 0}
            required
          >
            {relevantKeywordsFiles.map((file) => (
              <MenuItem 
                key={file.value} 
                value={file.path}
                sx={file.isSample ? {
                  color: 'text.secondary',
                  fontStyle: 'italic'
                } : {}}
              >
                {file.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {isLoadingFiles ? 'Loading files...' : 
             relevantKeywordsFiles.length > 0 ? 
             `${relevantKeywordsFiles.length} keyword file(s) available${usingMockData ? ' (using fallback paths)' : ''}` : 
             'No keyword files found'}
          </FormHelperText>
        </FormControl>
      </Box>
      
      <FormControlLabel
        control={
          <Checkbox 
            checked={forceReanalysis}
            onChange={(e) => onForceReanalysisChange(e.target.checked)}
          />
        }
        label="Force reanalysis"
        sx={{ my: 2 }}
      />
      
      {/* Divider before prompt editor */}
      <Divider sx={{ my: 2 }} />
      
      {/* Prompt Editor */}
      {onCustomPromptChange && (
        <Box sx={{ my: 2 }}>
          <Typography variant="subtitle1" fontWeight={500} mb={1}>
            Semantic Analysis Prompt Configuration
          </Typography>
          <PromptEditor
            module="module3"
            onPromptChange={onCustomPromptChange}
          />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}
      
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={isAnalyzing}
        fullWidth
        startIcon={isAnalyzing ? <CircularProgress size={20} /> : <AnalyticsIcon />}
        sx={{ mt: 2 }}
      >
        {isAnalyzing ? 'Analyzing...' : 'Start Analysis'}
      </Button>
    </Box>
  );
};

export default FileSelectionForm;
